import { AlgoliaHit } from 'instantsearch.js';
import { Minimum_Order_Value_Type_Enum, Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import { WishlistCompaniesByIdsQuery } from '../../queries/__generated__/wishlistCompaniesByIds.generated';
import { CompanyDeliveryZone } from '../../common/utils/common.utils';
import { extractSocialTagsFromDiscoveryHit } from '../../common/utils/social-tags.utils';
import { SupplierHit } from '../../search/search';
import { SupplierCardData } from './SupplierCard';

type Company = WishlistCompaniesByIdsQuery['company'][number];

function mapAlgoliaDeliveryZones(hit: SupplierHit): CompanyDeliveryZone[] {
  const deliveryTimeByArea = new Map(
    hit.delivery_time_per_delivery_zones?.map((areaDeliveryTime) => {
      const [area, time] = areaDeliveryTime.split('|');

      return [area, parseInt(time, 10)];
    }),
  );

  return (hit.mov_per_delivery_zones || []).map((areaMov) => {
    const [area, mov, movType] = areaMov.split('|');

    return {
      minimum_order_value: Number(mov),
      minimum_order_value_type: movType as Minimum_Order_Value_Type_Enum,
      negotiable_minimum_order_value: false,
      delivery_time: deliveryTimeByArea.get(area) ?? null,
      administrative_areas: [{ area }],
    };
  });
}

export function mapAlgoliaHitToSupplierCardData(hit: AlgoliaHit<SupplierHit>): SupplierCardData {
  return {
    id: hit.objectID,
    tradeName: hit.tradeName,
    businessProfile: hit.business_profile,
    photo: hit.photo,
    productFamilies: hit.product_families || [],
    isWholesaler: hit.is_wholesaler,
    socialTags: extractSocialTagsFromDiscoveryHit(hit),
    address: hit._geoloc
      ? {
        countryCode: hit.country_code,
        latitude: hit._geoloc.lat,
        // Algolia types lng but index stores lon — same workaround as DiscoveryCompanyHit
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        longitude: (hit._geoloc as any).lon || hit._geoloc.lng,
        formattedAddress: hit.formatted_address,
      }
      : undefined,
    deliveryZones: mapAlgoliaDeliveryZones(hit),
    partners: hit.partners || [],
  };
}

export function mapCompanyToSupplierCardData(
  company: Company,
): SupplierCardData {
  const address = company.addresses[0];
  const productFamilies = [...new Set(
    company.products
      .map(({ sub_family }) => sub_family as Product_Sub_Family_Name_Enum)
  )];

  return {
    id: company.id,
    tradeName: company.tradeName,
    businessProfile: company.business_profile,
    photo: company.photos[0]?.photo || null,
    productFamilies,
    isWholesaler: company.is_wholesaler,
    socialTags: company.social_tags.map(({ type, level, priority }) => ({
      type,
      level,
      priority,
    })),
    address: address
      ? {
        locality: address.locality,
        postalCode: address.postalCode,
        countryCode: address.countryCode,
        latitude: address.latitude,
        longitude: address.longitude,
        formattedAddress: address.formattedAddress,
      }
      : undefined,
    deliveryZones: company.delivery_zones.map((zone) => ({
      minimum_order_value: zone.minimum_order_value,
      minimum_order_value_type: zone.minimum_order_value_type,
      negotiable_minimum_order_value: zone.negotiable_minimum_order_value,
      delivery_time: zone.delivery_time,
      administrative_areas: zone.administrative_areas.map(({ area }) => ({ area })),
    })),
    partners: [],
  };
}
